import { type ReactNode } from 'react'

import { cn } from '../lib/cn'

export type BedTone = 'good' | 'warn' | 'bad'

export interface BedBadgeProps {
  /** Availability tone — the caller derives it (good ≥15% free, warn <15%, bad 0/none). */
  tone: BedTone
  /** Visible label, e.g. "ICU". The count is appended when `free` is given. */
  label: string
  free?: number
  total?: number
  className?: string
}

/**
 * Bed availability — ported from `FgAvailability` in `designs/system.jsx`
 * (~468), derived from node `65:4565`.
 *
 * THE STATUS WORD IS THE POINT. M16 put it in the atom with the note "the
 * status word is what carries the meaning when colour is unavailable", and
 * M17-T06 measured why it matters. Desaturated to Rec. 709 luma, the states
 * this badge has to separate collapse:
 *
 *   fills   accent-bg 236.9  good-bg 244.9  warn-bg 241.1  bad-bg 233.8
 *           → all four inside 11.1 of 255 grey steps; closest pair 3.0
 *   text    accent 72.0  good 51.3  warn 115.0  bad 39.7
 *           → good vs bad is 11.6 apart, the closest pair of the set — and it
 *             is the pair that matters most, because confusing "available"
 *             with "full" is the failure that sends someone to a hospital with
 *             no bed. accent vs good is 20.7.
 *
 * So after T02 colour carries almost nothing in greyscale. The ramp is not the
 * thing to change — it is a faithful port of the brand, and `--bed-good` is
 * brand green-700 by M16's standing decision. The word is what changes.
 * "3/8 · limited" reads correctly with every pixel grey, on a monochrome
 * print-out, and to a red-green colour-blind reader.
 *
 * The count is a second non-colour signal and stays: "0/8" says full on its
 * own. The dot is decorative and is marked as such.
 */
const TONE_CLASSES: Record<BedTone, string> = {
  good: 'text-bed-good bg-bed-good-bg ring-1 ring-inset ring-bed-good/15',
  warn: 'text-bed-warn bg-bed-warn-bg ring-1 ring-inset ring-bed-warn/15',
  bad: 'text-bed-bad bg-bed-bad-bg ring-1 ring-inset ring-bed-bad/15',
}
const DOT_CLASSES: Record<BedTone, string> = {
  good: 'bg-bed-good',
  warn: 'bg-bed-warn',
  bad: 'bg-bed-bad',
}
/** PORTED verbatim from FgAvailability. Do not shorten these to icons. */
const TONE_WORDS: Record<BedTone, string> = {
  good: 'available',
  warn: 'limited',
  bad: 'full',
}

export function BedBadge({
  tone,
  label,
  free,
  total,
  className,
}: BedBadgeProps): ReactNode {
  const hasCount = free !== undefined
  const count = hasCount
    ? ` ${free}${total !== undefined ? `/${total}` : ''}`
    : ''

  // Spelled out for a screen reader, which should not have to parse "4/8".
  const ariaLabel = hasCount
    ? total !== undefined
      ? `${label} ${free} free of ${total} — ${TONE_WORDS[tone]}`
      : `${label} ${free} free — ${TONE_WORDS[tone]}`
    : label

  return (
    <span
      // role="img" so the aria-label is legal and the badge is announced as one
      // thing. A bare span may not carry an accessible name (ARIA
      // aria-prohibited-attr), which the axe test below caught.
      role="img"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        TONE_CLASSES[tone],
        className,
      )}
    >
      <span
        className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASSES[tone])}
        aria-hidden="true"
      />
      <span aria-hidden="true" className="tabular-nums">
        {label}
        {count}
        {/* The status word only rides along where there is a count to qualify.
            A bare capability badge ("Ambulance") has no availability to state. */}
        {hasCount && (
          <span className="font-normal"> · {TONE_WORDS[tone]}</span>
        )}
      </span>
    </span>
  )
}
